/**
 * @fileoverview ClubOS Transfer Finance page — ISL transfer activity with amortisation
 * FC Metropolitan ISL Season 2025/26 — all values in INR.
 * TODO: Replace mock data with API calls: financeService.getTransfers(), financeService.recordTransfer()
 */

import { useState, useMemo, useEffect, useCallback } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Icon from "@mui/material/Icon";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftProgress from "components/SoftProgress";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// ClubOS utilities & data
import { formatCompact, formatDateIN, CLUB_NAME } from "utils/indiaLocale";
import { PermissionGate } from "components/PermissionGate";
import { financeService } from "api";
import FootballLoader from "components/FootballLoader";
import { useAuth } from "context/AuthContext";

// ── API → UI shape mapper ────────────────────────────────────────────────────
function mapApiTransfer(dto) {
  return {
    id:          dto.id,
    player:      dto.playerName,
    direction:   dto.transferType === "Outgoing" ? "Out" : "In",
    club:        dto.fromClub ?? dto.toClub ?? "—",
    fromClub:    dto.fromClub,
    toClub:      dto.toClub,
    fee:         dto.fixedFee,
    feeStructure:"Fixed",
    instalments: 1,
    addOns:      "",
    sellOnClause:dto.sellOnPercentage ?? 0,
    status:      dto.status,
    date:        dto.completionDate ?? "",
    window:      "ISL Summer",
    type:        dto.transferType,
    currency:    dto.currency,
    isDoFApproved:  dto.isDoFApproved,
    isBoardApproved:dto.isBoardApproved,
  };
}

const TRANSFER_BUDGET_REMAINING = 15_00_00_000; // fallback

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────

const STATUS_COLORS = { Completed: "success", Active: "info", Pending: "warning" };

/** ISL transfer windows */
const TRANSFER_WINDOWS = ["ISL Summer", "ISL Winter", "Emergency"];

/** Transfer types */
const TRANSFER_TYPES = ["Permanent", "Loan", "Free Transfer", "Academy Promotion"];

/** Empty form */
const EMPTY_FORM = {
  player: "",
  direction: "In",
  club: "",
  type: "Permanent",
  fee: "",
  instalments: "1",
  addOns: "",
  sellOnClause: "",
  date: "",
  window: "ISL Summer",
  status: "Completed",
};

/** Validate transfer form */
function validateForm(form) {
  const errors = {};
  if (!form.player.trim()) errors.player = "Player name is required";
  if (!form.club.trim()) errors.club = "Club name is required";
  if (!form.date) errors.date = "Transfer date is required";
  return errors;
}

/**
 * Generate a simple amortisation schedule for a transfer.
 * @param {number} fee - Total transfer fee in INR
 * @param {number} instalments - Number of equal instalments
 * @param {string} fromDate - Transfer date string
 * @returns {Array<{period: string, amount: number, cumulative: number}>}
 */
function buildAmortSchedule(fee, instalments, fromDate) {
  if (!fee || fee === 0) return [];
  const perInstalment = Math.floor(fee / instalments);
  const schedule = [];
  const start = new Date(fromDate || new Date());
  for (let i = 0; i < instalments; i++) {
    const payDate = new Date(start);
    payDate.setMonth(payDate.getMonth() + i * Math.floor(12 / instalments));
    const amount = i === instalments - 1 ? fee - perInstalment * (instalments - 1) : perInstalment;
    schedule.push({
      period: `Instalment ${i + 1}`,
      date: payDate.toLocaleDateString("en-IN", { month: "short", year: "numeric" }),
      amount,
      cumulative: perInstalment * (i + 1),
    });
  }
  return schedule;
}

// ─── COMPONENT ─────────────────────────────────────────────────────────────────

function Transfers() {
  const { currentUser } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0=All, 1=Incoming, 2=Outgoing

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const fetchTransfers = useCallback(() => {
    setLoading(true);
    financeService.getTransfers()
      .then((data) => setTransfers(data.map(mapApiTransfer)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  // ── Filtered transfers ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (tabValue === 1) return transfers.filter((t) => t.direction === "In");
    if (tabValue === 2) return transfers.filter((t) => t.direction === "Out");
    return transfers;
  }, [transfers, tabValue]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const inbound = transfers.filter((t) => t.direction === "In");
  const outbound = transfers.filter((t) => t.direction === "Out");
  const totalSpend  = inbound.reduce((acc, t) => acc + (t.fee || 0), 0);
  const totalIncome = outbound.reduce((acc, t) => acc + (t.fee || 0), 0);
  const netSpend    = totalSpend - totalIncome;
  const budgetLeft  = TRANSFER_BUDGET_REMAINING;

  // ── Amortisation for selected transfer ────────────────────────────────────
  const amortSchedule = selectedTransfer
    ? buildAmortSchedule(selectedTransfer.fee, selectedTransfer.instalments, selectedTransfer.date)
    : [];

  // ── Dialog handlers ───────────────────────────────────────────────────────
  const openAddDialog = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSave = async () => {
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setLoading(true);
    try {
      await financeService.createTransfer({
        playerId:        "00000000-0000-0000-0000-000000000000",  // placeholder — no player picker
        playerName:      form.player,
        transferType:    form.direction === "In" ? "Incoming" : "Outgoing",
        fixedFee:        Number(form.fee) || 0,
        currency:        "INR",
        fromClub:        form.direction === "In" ? form.club : null,
        toClub:          form.direction === "Out" ? form.club : null,
        sellOnPercentage:Number(form.sellOnClause) || 0,
        tenantId:        currentUser.tenantId,
      });
      await fetchTransfers();
      setDialogOpen(false);
    } catch (err) {
      setFormErrors({ submit: err?.title ?? err?.message ?? "Failed to save transfer" });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (transferId) => {
    if (!window.confirm("Cancel this transfer?")) return;
    try {
      await financeService.cancelTransfer(transferId, "Cancelled by user");
      setTransfers((prev) => prev.filter((t) => t.id !== transferId));
    } catch (err) {
      alert(err?.title ?? err?.message ?? "Failed to cancel transfer");
    }
  };

  const handleApprove = async (transferId, approvalLevel) => {
    try {
      await financeService.approveTransfer(transferId, approvalLevel);
      await fetchTransfers();
    } catch (err) {
      alert(err?.title ?? err?.message ?? "Failed to approve transfer");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              Transfer Finance
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Transfer window activity — {CLUB_NAME} · ISL Season 2025/26
            </SoftTypography>
          </SoftBox>
          <PermissionGate permission="finance.transfer.edit">
            <SoftButton variant="gradient" color="info" onClick={openAddDialog}>
              <Icon sx={{ mr: 0.5 }}>add</Icon>
              Record Transfer
            </SoftButton>
          </PermissionGate>
        </SoftBox>

        {/* ── KPI Cards ───────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Incoming Spend" }}
                count={formatCompact(totalSpend)}
                percentage={{ color: "error", text: `${inbound.length} signings` }}
                icon={{ color: "error", component: "arrow_downward" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Outgoing Received" }}
                count={formatCompact(totalIncome)}
                percentage={{ color: "success", text: `${outbound.length} departures` }}
                icon={{ color: "success", component: "arrow_upward" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Net Transfer Position" }}
                count={`-${formatCompact(netSpend)}`}
                percentage={{ color: "warning", text: "this window" }}
                icon={{ color: "warning", component: "balance" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Budget Remaining" }}
                count={formatCompact(budgetLeft)}
                percentage={{ color: "info", text: "available for transfers" }}
                icon={{ color: "info", component: "savings" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Transfer Table + Amortisation Panel ─────────────────────────── */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={selectedTransfer ? 7 : 12}>
            <Card>
              <SoftBox p={3}>
                {/* Tabs */}
                <SoftBox mb={2}>
                  <Tabs
                    value={tabValue}
                    onChange={(_, v) => { setTabValue(v); setSelectedTransfer(null); }}
                    sx={{ "& .MuiTab-root": { fontSize: "0.75rem", minWidth: 100 } }}
                  >
                    <Tab label="All Transfers" />
                    <Tab label="Incoming" />
                    <Tab label="Outgoing" />
                  </Tabs>
                </SoftBox>

                {loading ? (
                  <FootballLoader label="Loading transfers…" />
                ) : (
                  <SoftBox sx={{ overflowX: "auto" }}>
                    <SoftBox
                      component="table"
                      sx={{ width: "100%", borderCollapse: "collapse", minWidth: 650 }}
                    >
                      <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                        <SoftBox component="tr">
                          {["Player", "Dir.", "Club", "Fee", "Type", "Instalments", "Add-ons", "Status", "Date", ""].map((h) => (
                            <SoftBox
                              key={h}
                              component="th"
                              sx={{
                                textAlign: "left",
                                p: 1.5,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                textTransform: "uppercase",
                                color: "text.secondary",
                                borderBottom: "2px solid #e9ecef",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {h}
                            </SoftBox>
                          ))}
                        </SoftBox>
                      </SoftBox>
                      <SoftBox component="tbody">
                        {filtered.length === 0 ? (
                          <SoftBox component="tr">
                            <SoftBox component="td" colSpan={10} sx={{ textAlign: "center", p: 4 }}>
                              <SoftTypography variant="caption" color="text">
                                No transfers in this view.
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        ) : (
                          filtered.map((t, i) => (
                            <SoftBox
                              key={t.id}
                              component="tr"
                              sx={{
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "#f8f9fa" },
                                backgroundColor:
                                  selectedTransfer?.id === t.id ? "#eef6ff" : i % 2 === 0 ? "white" : "#fdfdfd",
                              }}
                              onClick={() => setSelectedTransfer(t.id === selectedTransfer?.id ? null : t)}
                            >
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftTypography variant="button" fontWeight="medium">{t.player}</SoftTypography>
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftBadge
                                  badgeContent={t.direction}
                                  color={t.direction === "In" ? "success" : "error"}
                                  size="xs"
                                  container
                                />
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftTypography variant="caption" color="text">{t.club}</SoftTypography>
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftTypography variant="button" fontWeight="bold">
                                  {t.fee === 0 ? "Free" : formatCompact(t.fee)}
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftTypography variant="caption" color="text">{t.type || t.feeStructure}</SoftTypography>
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftTypography variant="caption" color="text">{t.instalments}</SoftTypography>
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", maxWidth: 120 }}>
                                <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                                  {t.addOns}
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftBadge
                                  badgeContent={t.status}
                                  color={STATUS_COLORS[t.status] || "info"}
                                  size="xs"
                                  container
                                />
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftTypography variant="caption" color="text">
                                  {formatDateIN(t.date)}
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                                <SoftButton variant="text" color="info" size="small">
                                  {selectedTransfer?.id === t.id ? "Close" : "Details"}
                                </SoftButton>
                              </SoftBox>
                            </SoftBox>
                          ))
                        )}
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                )}
              </SoftBox>
            </Card>
          </Grid>

          {/* ── Amortisation Panel ─────────────────────────────────────────── */}
          {selectedTransfer && (
            <Grid item xs={12} lg={5}>
              <Card>
                <SoftBox p={3}>
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <SoftTypography variant="h6" fontWeight="medium">
                      Amortisation
                    </SoftTypography>
                    <SoftButton
                      variant="text"
                      color="secondary"
                      size="small"
                      onClick={() => setSelectedTransfer(null)}
                    >
                      <Icon>close</Icon>
                    </SoftButton>
                  </SoftBox>

                  <SoftTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                    {selectedTransfer.player}
                  </SoftTypography>
                  <SoftBox display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <SoftBadge
                      badgeContent={selectedTransfer.direction === "In" ? "Incoming" : "Outgoing"}
                      color={selectedTransfer.direction === "In" ? "success" : "error"}
                      size="xs"
                      container
                    />
                    <SoftBadge
                      badgeContent={selectedTransfer.club}
                      color="info"
                      size="xs"
                      container
                    />
                  </SoftBox>

                  <SoftBox mb={2}>
                    <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                      <SoftTypography variant="caption" color="text">Total Fee</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold">
                        {selectedTransfer.fee === 0 ? "Free Transfer" : formatCompact(selectedTransfer.fee)}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                      <SoftTypography variant="caption" color="text">Instalments</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold">{selectedTransfer.instalments}</SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="space-between">
                      <SoftTypography variant="caption" color="text">Transfer Date</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold">
                        {formatDateIN(selectedTransfer.date)}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>

                  {selectedTransfer.fee > 0 ? (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                        Instalment Schedule
                      </SoftTypography>
                      {amortSchedule.map((row, idx) => {
                        const progressPct = Math.round((row.cumulative / selectedTransfer.fee) * 100);
                        return (
                          <SoftBox key={row.period} mb={1.5}>
                            <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                              <SoftTypography variant="caption" fontWeight="medium">
                                {row.period} — {row.date}
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                {formatCompact(row.amount)}
                              </SoftTypography>
                            </SoftBox>
                            <SoftProgress
                              value={Math.min(progressPct, 100)}
                              color={idx === amortSchedule.length - 1 ? "success" : "info"}
                            />
                          </SoftBox>
                        );
                      })}
                    </>
                  ) : (
                    <SoftTypography variant="caption" color="text">
                      Free transfer — no amortisation or instalment schedule required.
                    </SoftTypography>
                  )}

                  {selectedTransfer.addOns && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        Add-on Conditions
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text">
                        {selectedTransfer.addOns}
                      </SoftTypography>
                    </>
                  )}

                  {selectedTransfer.sellOnClause > 0 && (
                    <SoftBox mt={1}>
                      <SoftTypography variant="caption" color="text">
                        Sell-on clause: <strong>{selectedTransfer.sellOnClause}%</strong>
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </SoftBox>

      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />

      {/* ── Add Transfer Dialog ──────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          Record Transfer
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pt={1}>
            {/* Player Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Player Name"
                required
                value={form.player}
                onChange={(e) => handleFormChange("player", e.target.value)}
                error={!!formErrors.player}
                helperText={formErrors.player}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Direction */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Direction</InputLabel>
                <Select value={form.direction} label="Direction" onChange={(e) => handleFormChange("direction", e.target.value)}>
                  <MenuItem value="In">Incoming</MenuItem>
                  <MenuItem value="Out">Outgoing</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Transfer Type */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={form.type} label="Type" onChange={(e) => handleFormChange("type", e.target.value)}>
                  {TRANSFER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Club */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={form.direction === "In" ? "From Club *" : "To Club *"}
                value={form.club}
                onChange={(e) => handleFormChange("club", e.target.value)}
                error={!!formErrors.club}
                helperText={formErrors.club}
                size="small"
                placeholder="e.g. Mumbai City FC"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Base Transfer Fee */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Base Transfer Fee (₹)"
                type="number"
                value={form.fee}
                onChange={(e) => handleFormChange("fee", e.target.value)}
                size="small"
                inputProps={{ min: 0 }}
                helperText={form.fee ? formatCompact(Number(form.fee)) : "0 = Free Transfer"}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Instalments */}
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="Instalments"
                type="number"
                value={form.instalments}
                onChange={(e) => handleFormChange("instalments", e.target.value)}
                size="small"
                inputProps={{ min: 1, max: 5 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Sell-on Clause */}
            <Grid item xs={6} sm={1}>
              <TextField
                fullWidth
                label="Sell-on %"
                type="number"
                value={form.sellOnClause}
                onChange={(e) => handleFormChange("sellOnClause", e.target.value)}
                size="small"
                inputProps={{ min: 0, max: 50 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Add-on Conditions */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Add-on Conditions"
                placeholder="e.g. ₹1 Cr on 20 ISL starts, ₹50L on India national team debut"
                value={form.addOns}
                onChange={(e) => handleFormChange("addOns", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Transfer Date */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Transfer Date"
                required
                type="date"
                value={form.date}
                onChange={(e) => handleFormChange("date", e.target.value)}
                error={!!formErrors.date}
                helperText={formErrors.date}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Transfer Window */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Transfer Window</InputLabel>
                <Select value={form.window} label="Transfer Window" onChange={(e) => handleFormChange("window", e.target.value)}>
                  {TRANSFER_WINDOWS.map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={(e) => handleFormChange("status", e.target.value)}>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave}>
            Record Transfer
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Transfers;
